import {
	DocumentSigningScope,
	InvestmentProgrammeDocumentDetailsRes,
	SignatureType,
} from '@dealstack/shared/lib/generated';
import { isClaimedSignerGenerated } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/Documents/hooks/useInvestmentProgrammeDocument.utils';
import {
	PendingDocumentAction,
	PendingDocumentActionType,
	PendingSignerAssignmentAction,
} from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/Documents/hooks/usePendingDocumentAction.types';

export const hasPendingActionSettled = ({
	action,
	documentDetails,
}: {
	action: PendingDocumentAction;
	documentDetails: InvestmentProgrammeDocumentDetailsRes | undefined;
}): boolean => {
	if (!documentDetails) {
		return false;
	}

	if (action.action === PendingDocumentActionType.DOCUMENT_REFRESH) {
		return (
			documentDetails.updatedAt != null &&
			action.previousUpdatedAt != null &&
			documentDetails.updatedAt !== action.previousUpdatedAt
		);
	}

	if (action.action === PendingDocumentActionType.CLAIM_GENERATION) {
		return isClaimedSignerGenerated(documentDetails, action.signerId);
	}

	const group = documentDetails.groups.find((candidate) => candidate.roleId === action.roleId);
	if (!group) {
		return false;
	}

	const signer = group.signers.find(
		(candidate) => candidate.userId === action.userId && candidate.assigned
	);

	if (action.action === PendingDocumentActionType.UNASSIGN) {
		return !signer?.assigned;
	}

	if (!signer) {
		return false;
	}

	// ASSIGN. Only a wet-ink signer in a combined document waits on anything: its
	// signature page is regenerated asynchronously. The comparison is against the
	// pre-action snapshot, because a reassignment replaces an existing file rather
	// than filling an empty one.
	//
	// Electronic never reaches here — `beginPendingAction` short-circuits those to
	// a plain refetch, the signing URL being created inside the request.
	if (
		documentDetails.signatureType === SignatureType.WET_INK &&
		documentDetails.signingScope === DocumentSigningScope.ALL_PARTICIPANTS
	) {
		return (
			!!signer.fileId &&
			(action.previousSignerFileId != null
				? signer.fileId !== action.previousSignerFileId
				: true)
		);
	}

	// Any other scope needs no generated artefact — assignment alone makes the
	// signer ready.
	return true;
};

/** Assign and unassign are the actions that gate the per-group signer UI. */
export const isSignerAssignmentAction = (
	action: PendingDocumentAction | null
): action is PendingSignerAssignmentAction =>
	!!action &&
	(action.action === PendingDocumentActionType.ASSIGN ||
		action.action === PendingDocumentActionType.UNASSIGN);
